// src/pages/Reports/HrAbsencePayrollReportPage.js
//
// HR Absence Payroll Report
// ============================================================================
// PURPOSE
// -------
// Read-only payroll-oriented absence report for accounting / period closing.
// The page intentionally lives under the Reports module, not inside the
// operational HR workflow, because it consumes HR data but does not manage leave
// requests.
//
// ARCHITECTURAL SAFETY BOUNDARY
// -----------------------------
// This page must remain a reporting consumer only:
// - it does not create leave requests,
// - it does not approve/reject/cancel requests,
// - it does not recalculate balances,
// - it does not alter entitlement contexts,
// - it does not touch MAKE_UP_WORK / PRIVATE_EXIT_TO_MAKE_UP settlement logic.
//
// It follows the same UX pattern as Commission Report:
// - shared EvaluationPeriodPicker,
// - optional multi-employee selector,
// - Generate button,
// - PDF orientation selector,
// - PDF export.
//
// Current backend contract:
//   GET /reports/hr/absence-payroll?from=YYYY-MM-DD&to=YYYY-MM-DD&userIds=1,2
//
// Access is protected at two levels:
// - React route is restricted to superuser,
// - backend endpoint is restricted to superuser.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchHrAbsencePayrollReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import "./HrAbsencePayrollReportPage.css";

/* -------------------------------------------------------------------------- */
/* Formatting helpers                                                         */
/* -------------------------------------------------------------------------- */

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatNumber(value, decimals = 2) {
  return toNumber(value, 0).toFixed(decimals);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "—";
  return `${formatNumber(value, 2)}%`;
}

function formatCell(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
}

function displayEmployee(user) {
  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";
  const username = user?.username || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  if (fullName && username) return `${fullName} (${username})`;
  if (fullName) return fullName;
  if (username) return username;
  return `#${user?.id || "?"}`;
}

/* -------------------------------------------------------------------------- */
/* Report calculations                                                        */
/* -------------------------------------------------------------------------- */

function buildEmployeeTotals(rows) {
  const map = new Map();

  for (const row of rows) {
    const key = row.employee_id || row.employee_username || row.employee_name || "unknown";
    const current = map.get(key) || {
      employeeName: row.employee_name || row.employee_username || "—",
      employeeUsername: row.employee_username || "—",
      hours: 0,
      fteDays: 0,
      paidHoursEquivalent: 0,
      lines: 0,
    };

    current.hours += toNumber(row.duration_hours, 0);
    current.fteDays += toNumber(row.duration_days_equivalent, 0);
    current.paidHoursEquivalent += toNumber(row.paid_hours_equivalent, 0);
    current.lines += 1;

    map.set(key, current);
  }

  return Array.from(map.values()).sort((a, b) =>
    String(a.employeeName).localeCompare(String(b.employeeName))
  );
}

function buildExceptions(rows) {
  const result = [];

  for (const row of rows) {
    const reasons = [];

    if (!row.payroll_absence_code) {
      reasons.push("missing_payroll_code");
    }

    if (row.payroll_payment_rate_percent === null || row.payroll_payment_rate_percent === undefined) {
      reasons.push("missing_payment_rate");
    }

    if (String(row.entitlement_context_type || "").toUpperCase().includes("SICK")) {
      if (!row.entitlement_sick_benefit_payer || row.entitlement_sick_benefit_payer === "UNKNOWN") {
        reasons.push("sick_leave_benefit_payer_requires_review");
      }
    }

    if (reasons.length > 0) {
      result.push({
        leaveRequestId: row.leave_request_id,
        employeeName: row.employee_name,
        workDate: row.work_date,
        leaveTypeCode: row.leave_type_code,
        payrollAbsenceCode: row.payroll_absence_code,
        reasons,
      });
    }
  }

  return result;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const HrAbsencePayrollReportPage = () => {
  const { t } = useTranslation(["reports", "common"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  const allowedEvaluationModes = useMemo(
    () => [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ],
    []
  );

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });
  const [pdfOrientation, setPdfOrientation] = useState("l");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[HrAbsencePayrollReportPage] Failed to fetch users", err);
        setError(t("reports:hrAbsencePayroll.errors.failedToLoadUsers"));
      }
    };

    loadUsers();
  }, [t]);

  const currentUserDisplay = useMemo(() => {
    return displayEmployee(authState?.user) || t("reports:common.currentUser");
  }, [authState?.user, t]);

  const rows = useMemo(() => asArray(report?.rows), [report]);
  const employeeTotals = useMemo(() => buildEmployeeTotals(rows), [rows]);
  const exceptions = useMemo(() => buildExceptions(rows), [rows]);

  const totalHours = useMemo(
    () => rows.reduce((sum, row) => sum + toNumber(row.duration_hours, 0), 0),
    [rows]
  );

  const totalFteDays = useMemo(
    () => rows.reduce((sum, row) => sum + toNumber(row.duration_days_equivalent, 0), 0),
    [rows]
  );

  const totalPaidHoursEquivalent = useMemo(
    () => rows.reduce((sum, row) => sum + toNumber(row.paid_hours_equivalent, 0), 0),
    [rows]
  );

  const hasRows = rows.length > 0;

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        setReport(null);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const data = await fetchHrAbsencePayrollReport(from, to, userIds);
        setReport(data || null);
        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[HrAbsencePayrollReportPage] Failed to load report", err);
        setError(t("reports:hrAbsencePayroll.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  const generatePDF = () => {
    if (!report) return;

    const doc = new jsPDF(pdfOrientation, "mm", "a4");
    const from = loadedPeriodMeta?.from || report?.from || "N/A";
    const to = loadedPeriodMeta?.to || report?.to || "N/A";
    const displayLabel = loadedPeriodMeta?.displayLabel || "";

    doc.setFontSize(16);
    doc.text(t("reports:hrAbsencePayroll.pdfTitle"), 14, 12);

    doc.setFontSize(11);
    doc.text(t("reports:common.fromToPdfLine", { from, to }), 14, 19);

    let cursorY = 26;
    if (displayLabel) {
      doc.setFontSize(10);
      doc.text(t("reports:common.evaluationPeriodPdfLine", { displayLabel }), 14, cursorY);
      cursorY += 7;
    }

    autoTable(doc, {
      startY: cursorY,
      head: [[t("reports:common.metric"), t("reports:common.value")]],
      body: [
        [t("reports:hrAbsencePayroll.summary.lines"), rows.length],
        [t("reports:hrAbsencePayroll.summary.employees"), employeeTotals.length],
        [t("reports:hrAbsencePayroll.summary.hours"), formatNumber(totalHours)],
        [t("reports:hrAbsencePayroll.summary.fteDays"), formatNumber(totalFteDays)],
        [t("reports:hrAbsencePayroll.summary.paidHours"), formatNumber(totalPaidHoursEquivalent)],
        [t("reports:hrAbsencePayroll.summary.exceptions"), exceptions.length],
      ],
      theme: "grid",
      styles: { fontSize: 9 },
      headStyles: { fontStyle: "bold" },
      margin: { left: 14, right: 14 },
    });

    cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;

    doc.setFontSize(12);
    doc.text(t("reports:hrAbsencePayroll.employeeTotals.title"), 14, cursorY);

    autoTable(doc, {
      startY: cursorY + 4,
      head: [[
        t("reports:hrAbsencePayroll.columns.employee"),
        t("reports:hrAbsencePayroll.columns.username"),
        t("reports:hrAbsencePayroll.columns.lines"),
        t("reports:hrAbsencePayroll.columns.hours"),
        t("reports:hrAbsencePayroll.columns.fteDays"),
        t("reports:hrAbsencePayroll.columns.paidHours"),
      ]],
      body: employeeTotals.map((row) => [
        formatCell(row.employeeName),
        formatCell(row.employeeUsername),
        row.lines,
        formatNumber(row.hours),
        formatNumber(row.fteDays),
        formatNumber(row.paidHoursEquivalent),
      ]),
      theme: "striped",
      styles: { fontSize: 8 },
      headStyles: { fontStyle: "bold" },
      margin: { left: 14, right: 14 },
    });

    cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;

    doc.setFontSize(12);
    doc.text(t("reports:hrAbsencePayroll.lines.title"), 14, cursorY);

    autoTable(doc, {
      startY: cursorY + 4,
      head: [[
        t("reports:hrAbsencePayroll.columns.employee"),
        t("reports:hrAbsencePayroll.columns.requestId"),
        t("reports:hrAbsencePayroll.columns.workDate"),
        t("reports:hrAbsencePayroll.columns.leaveType"),
        t("reports:hrAbsencePayroll.columns.payrollCode"),
        t("reports:hrAbsencePayroll.columns.paymentRate"),
        t("reports:hrAbsencePayroll.columns.hours"),
        t("reports:hrAbsencePayroll.columns.fteDays"),
        t("reports:hrAbsencePayroll.columns.benefitPayer"),
      ]],
      body: rows.map((row) => [
        formatCell(row.employee_name),
        formatCell(row.leave_request_id),
        formatCell(row.work_date),
        formatCell(row.leave_type_code),
        formatCell(row.payroll_absence_code),
        formatPercent(row.payroll_payment_rate_percent),
        formatNumber(row.duration_hours),
        formatNumber(row.duration_days_equivalent),
        formatCell(row.entitlement_sick_benefit_payer),
      ]),
      theme: "striped",
      styles: { fontSize: 7, cellPadding: 1.6 },
      headStyles: { fontStyle: "bold" },
      margin: { left: 8, right: 8 },
    });

    if (exceptions.length > 0) {
      cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;

      if (cursorY > 250) {
        doc.addPage();
        cursorY = 16;
      }

      doc.setFontSize(12);
      doc.text(t("reports:hrAbsencePayroll.exceptions.title"), 14, cursorY);

      autoTable(doc, {
        startY: cursorY + 4,
        head: [[
          t("reports:hrAbsencePayroll.columns.employee"),
          t("reports:hrAbsencePayroll.columns.requestId"),
          t("reports:hrAbsencePayroll.columns.workDate"),
          t("reports:hrAbsencePayroll.columns.leaveType"),
          t("reports:hrAbsencePayroll.columns.reason"),
        ]],
        body: exceptions.map((row) => [
          formatCell(row.employeeName),
          formatCell(row.leaveRequestId),
          formatCell(row.workDate),
          formatCell(row.leaveTypeCode),
          row.reasons.map((reason) => t(`reports:hrAbsencePayroll.exceptionReasons.${reason}`)).join(", "),
        ]),
        theme: "grid",
        styles: { fontSize: 8 },
        headStyles: { fontStyle: "bold" },
        margin: { left: 14, right: 14 },
      });
    }

    doc.save(`HR_Absence_Payroll_${from}_${to}.pdf`);
  };

  return (
    <div className="hr-payroll-report-page">
      <header className="hr-payroll-report-header">
        <div>
          <p className="hr-payroll-report-kicker">{t("reports:hrAbsencePayroll.kicker")}</p>
          <h1>{t("reports:hrAbsencePayroll.title")}</h1>
          <p className="hr-payroll-report-description">
            {t("reports:hrAbsencePayroll.description")}
          </p>
        </div>
      </header>

      <section className="hr-payroll-report-toolbar" aria-label={t("reports:hrAbsencePayroll.filtersAria")}>
        <div className="hr-payroll-report-toolbar-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="hr-payroll-report-field hr-payroll-report-field--users">
              <label>{t("reports:common.employees")}</label>
              <select
                multiple
                className="hr-payroll-report-user-dropdown"
                value={filters.userIds}
                onChange={(event) => {
                  const selectedValues = Array.from(
                    event.target.selectedOptions,
                    (option) => Number(option.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {displayEmployee(user)}
                  </option>
                ))}
              </select>
              <div className="hr-payroll-report-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="hr-payroll-report-field hr-payroll-report-field--users">
              <label>{t("reports:common.employee")}</label>
              <input type="text" value={currentUserDisplay} readOnly />
            </div>
          )}
        </div>

        <div className="hr-payroll-report-toolbar-actions">
          <div className="hr-payroll-report-field hr-payroll-report-field--pdf">
            <label>{t("reports:common.pdf")}</label>
            <select
              value={pdfOrientation}
              onChange={(event) => setPdfOrientation(event.target.value)}
            >
              <option value="p">{t("reports:common.portrait")}</option>
              <option value="l">{t("reports:common.landscape")}</option>
            </select>
          </div>

          <button
            type="button"
            className="hr-payroll-report-primary-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>

          <button
            type="button"
            className="hr-payroll-report-secondary-button"
            onClick={generatePDF}
            disabled={!report || loading}
          >
            {t("reports:common.exportPdf")}
          </button>
        </div>
      </section>

      {error && <div className="hr-payroll-report-error">{error}</div>}

      {!report && !error && (
        <div className="hr-payroll-report-empty-state">
          {t("reports:hrAbsencePayroll.emptyState")}
        </div>
      )}

      {report && (
        <main className="hr-payroll-report-content">
          <section className="hr-payroll-report-summary-grid">
            <article className="hr-payroll-report-summary-card">
              <span>{t("reports:hrAbsencePayroll.summary.period")}</span>
              <strong>{loadedPeriodMeta.displayLabel || `${report.from} – ${report.to}`}</strong>
            </article>
            <article className="hr-payroll-report-summary-card">
              <span>{t("reports:hrAbsencePayroll.summary.lines")}</span>
              <strong>{rows.length}</strong>
            </article>
            <article className="hr-payroll-report-summary-card">
              <span>{t("reports:hrAbsencePayroll.summary.employees")}</span>
              <strong>{employeeTotals.length}</strong>
            </article>
            <article className="hr-payroll-report-summary-card">
              <span>{t("reports:hrAbsencePayroll.summary.hours")}</span>
              <strong>{formatNumber(totalHours)}</strong>
            </article>
            <article className="hr-payroll-report-summary-card">
              <span>{t("reports:hrAbsencePayroll.summary.fteDays")}</span>
              <strong>{formatNumber(totalFteDays)}</strong>
            </article>
            <article className="hr-payroll-report-summary-card">
              <span>{t("reports:hrAbsencePayroll.summary.paidHours")}</span>
              <strong>{formatNumber(totalPaidHoursEquivalent)}</strong>
            </article>
            <article className="hr-payroll-report-summary-card hr-payroll-report-summary-card--warning">
              <span>{t("reports:hrAbsencePayroll.summary.exceptions")}</span>
              <strong>{exceptions.length}</strong>
            </article>
          </section>

          <section className="hr-payroll-report-section">
            <div className="hr-payroll-report-section-header">
              <h2>{t("reports:hrAbsencePayroll.employeeTotals.title")}</h2>
              <p>{t("reports:hrAbsencePayroll.employeeTotals.description")}</p>
            </div>

            <div className="hr-payroll-report-table-wrap">
              <table className="hr-payroll-report-table">
                <thead>
                  <tr>
                    <th>{t("reports:hrAbsencePayroll.columns.employee")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.username")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.lines")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.hours")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.fteDays")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.paidHours")}</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeTotals.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="hr-payroll-report-empty-cell">
                        {t("reports:common.noDataAvailable")}
                      </td>
                    </tr>
                  ) : (
                    employeeTotals.map((row) => (
                      <tr key={`${row.employeeName}-${row.employeeUsername}`}>
                        <td>{formatCell(row.employeeName)}</td>
                        <td>{formatCell(row.employeeUsername)}</td>
                        <td>{row.lines}</td>
                        <td>{formatNumber(row.hours)}</td>
                        <td>{formatNumber(row.fteDays)}</td>
                        <td>{formatNumber(row.paidHoursEquivalent)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="hr-payroll-report-section">
            <div className="hr-payroll-report-section-header">
              <h2>{t("reports:hrAbsencePayroll.lines.title")}</h2>
              <p>{t("reports:hrAbsencePayroll.lines.description")}</p>
            </div>

            <div className="hr-payroll-report-table-wrap hr-payroll-report-table-wrap--large">
              <table className="hr-payroll-report-table hr-payroll-report-table--dense">
                <thead>
                  <tr>
                    <th>{t("reports:hrAbsencePayroll.columns.employee")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.requestId")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.workDate")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.leaveType")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.payrollCode")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.paymentRate")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.hours")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.fteDays")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.benefitPayer")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.context")}</th>
                  </tr>
                </thead>
                <tbody>
                  {hasRows ? (
                    rows.map((row, index) => (
                      <tr key={`${row.leave_request_id}-${row.work_date}-${index}`}>
                        <td>{formatCell(row.employee_name)}</td>
                        <td>{formatCell(row.leave_request_id)}</td>
                        <td>{formatCell(row.work_date)}</td>
                        <td>
                          <strong>{formatCell(row.leave_type_code)}</strong>
                          <span>{formatCell(row.leave_type_name_en)}</span>
                        </td>
                        <td>{formatCell(row.payroll_absence_code)}</td>
                        <td>{formatPercent(row.payroll_payment_rate_percent)}</td>
                        <td>{formatNumber(row.duration_hours)}</td>
                        <td>{formatNumber(row.duration_days_equivalent)}</td>
                        <td>{formatCell(row.entitlement_sick_benefit_payer)}</td>
                        <td>{formatCell(row.entitlement_context_type)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="hr-payroll-report-empty-cell">
                        {t("reports:common.noDataAvailable")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="hr-payroll-report-section">
            <div className="hr-payroll-report-section-header">
              <h2>{t("reports:hrAbsencePayroll.exceptions.title")}</h2>
              <p>{t("reports:hrAbsencePayroll.exceptions.description")}</p>
            </div>

            <div className="hr-payroll-report-table-wrap">
              <table className="hr-payroll-report-table">
                <thead>
                  <tr>
                    <th>{t("reports:hrAbsencePayroll.columns.employee")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.requestId")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.workDate")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.leaveType")}</th>
                    <th>{t("reports:hrAbsencePayroll.columns.reason")}</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptions.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="hr-payroll-report-empty-cell">
                        {t("reports:hrAbsencePayroll.exceptions.none")}
                      </td>
                    </tr>
                  ) : (
                    exceptions.map((row, index) => (
                      <tr key={`${row.leaveRequestId}-${row.workDate}-${index}`}>
                        <td>{formatCell(row.employeeName)}</td>
                        <td>{formatCell(row.leaveRequestId)}</td>
                        <td>{formatCell(row.workDate)}</td>
                        <td>{formatCell(row.leaveTypeCode)}</td>
                        <td>{row.reasons.map((reason) => t(`reports:hrAbsencePayroll.exceptionReasons.${reason}`)).join(", ")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      )}
    </div>
  );
};

export default HrAbsencePayrollReportPage;
